import type { RecoveryConfig } from 'api/recoveryAi.types'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import {
  AgentSettingsCard,
  AgentSettingsRow,
} from '../components/AgentSettingsCard'

const t = buildTranslate('recovery_ai.sandbox.agent_settings')
// Posture labels are owned by the decision log block — reuse them so the two
// surfaces never drift.
const tDecisionLog = buildTranslate('recovery_ai.sandbox.decision_log')

type Props = {
  config: Pick<RecoveryConfig, 'brand_voice_mode' | 'philosophy_posture'>
  // AI-204: 'wizard' adds the subtitle and renames the generosity row. The card/row
  // CHROME no longer forks — AgentSettingsCard is one treatment for both surfaces
  // (plan §1.6), so this variant is down to copy alone.
  variant?: 'default' | 'wizard'
  // AI-202: the grantable-reward count (active ∩ approved). Supplied by the shell
  // from useSimulatorData().approvedRewards — the single source the reward cards
  // also derive from — so the count and the cards can't drift (SimulatorEngine
  // forwards it). Defaults to 0 for a caller that renders before it resolves.
  rewardCount?: number
}

// READ-ONLY summary of the agent's brand voice / generosity posture / approved
// reward count (AI-95). Renders no interactive controls and owns NO data fetching —
// the grantable count arrives as a prop from the shell's single useSimulatorData
// source (AI-202), so it can never diverge from the reward cards the simulator renders.
//
//   context: NEITHER shipped surface renders this today — the wizard and the Agent
//   Tuning tab both pass an interactive `settingsCard` (AgentTuningConfigModals),
//   which replaces it. It survives for the full-page standalone route, and it shares
//   AgentSettingsCard with that interactive host so the two cannot drift apart again.
const AgentSettingsSummary = ({
  config,
  variant = 'default',
  rewardCount = 0,
}: Props) => {
  const [css] = useStyletron()
  const isWizard = variant === 'wizard'

  return (
    <AgentSettingsCard
      title={t('title')}
      description={isWizard ? t('description') : undefined}
    >
      {/* dl/dt/dd so a screen reader pairs each label with its value; divs alone
          convey the relationship only by layout (WCAG 1.3.1). The interactive host
          can't use this shape — see AgentSettingsRow's `onClick` docs. */}
      <dl className={css({ margin: 0 })}>
        <AgentSettingsRow
          label={t('brand_voice_label')}
          value={t(`brand_voice_values.${config.brand_voice_mode}`)}
        />
        <AgentSettingsRow
          label={isWizard ? t('posture_label_recovery') : t('posture_label')}
          value={tDecisionLog(`posture_values.${config.philosophy_posture}`)}
        />
        <AgentSettingsRow
          label={t('rewards_label')}
          value={t('rewards', { count: rewardCount })}
        />
      </dl>
    </AgentSettingsCard>
  )
}

export default AgentSettingsSummary
